import type { BookableResourceRef } from "./bookable-resource";
import type { BookingCheck } from "./booking";
import type { CaseId } from "./case.model";
import type { CodeableConcept } from "./common";
import { InvalidTransitionError, PreconditionFailedError, SlotConflictError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import { asMemberId, highWaterMark, type MemberId, memberIdSequence } from "./member-identity";
import type { OrderSetId } from "./order-set.model";
import type { PatientId } from "./patient.model";
import type { PractitionerId } from "./practitioner.model";
import type { ScheduleId } from "./schedule.model";
import { TimeRange, type TimeRangeProps } from "./time-range";

/** Stable Visit domain identity, independent of the Appointment locator. */
export type VisitId = DomainId<"VisitId">;

export function asVisitId(value: string): VisitId {
  if (!value) throw new PreconditionFailedError("A Visit identity cannot be empty");
  return value as VisitId;
}

// ============================================================
// Visit — first-principles Visit aggregate (ADR-0017)
//
// Novel domain shape: the FHIR decomposition (Appointment + Slot +
// Schedule + Encounter) is the mapper's business. Identity remains at
// the joints only: assignment.id and execution.id are stable parent-scoped
// member identities; calendarId = Schedule id (the bookable diary is a domain concept).
//
// Surface rule: operations and facts, never guards. Preconditions live
// inside the operation they protect or as a condition of access.
// ============================================================

export const VISIT_MODALITIES = ["in-person", "virtual"] as const;
export type VisitModality = (typeof VISIT_MODALITIES)[number];

/**
 * Virtual visits wait in `checkedIn` for conference admission. In-person
 * check-in moves directly from `booked` to `inProgress`.
 */
export type VisitStatus = "proposed" | "booked" | "checkedIn" | "inProgress" | "completed" | "cancelled" | "noShow";

/** Canonical member identity for a newly-created Visit execution. */
export const DEFAULT_VISIT_EXECUTION_MEMBER_ID = asMemberId("execution-1");

/**
 * How the visit's work came to be — provenance, and nothing about the work.
 *
 * The visit's link to its order set IS its link to the clinical work, so the
 * protocol, the session key and the procedures are all read through the set
 * rather than copied onto the visit. What remains is the one fact the graph
 * cannot answer: whether this booking minted its own orders (`standalone`) or
 * booked orders a curation had already committed (`curated-session`).
 *
 * Stored, not derived, because it must be stable for the visit's life. A
 * rescheduled standalone visit re-books the set it already had, which makes it
 * structurally identical to a curated session — "has a set" cannot tell them
 * apart, and during a partial booking it cannot tell either from a consult.
 */
export const VISIT_KINDS = ["consultation", "standalone", "curated-session"] as const;
type VisitKind = (typeof VISIT_KINDS)[number];

// ── Children ──────────────────────────────────────────────────

/** One concept where the FHIR graph needs three (participant + Slot + Schedule). */
export interface ResourceAssignment {
  /** Stable member identity within this visit's OrderSet. Absent on envelope/consultation claims. */
  stepId?: MemberId;
  /** Stable parent-scoped identity; the Slot locator is mapper-private. */
  id: MemberId;
  resource: BookableResourceRef;
  /** Schedule id — the diary this reservation books against. */
  calendarId: ScheduleId;
  window: TimeRangeProps;
}

/**
 * What the visit needs to know about a case to accept a link to it.
 *
 * Evidence, not a verdict, and deliberately not the Case: whether a case
 * is open is the case's own conclusion about its own status, and the visit
 * neither knows nor should learn that vocabulary.
 */
export interface CaseLinkFacts {
  caseIsOpen: boolean;
}

/** Execution record — the Encounter's essence, not its shape. Present from booking. */
interface Execution {
  /** Stable parent-scoped identity; the Encounter locator is mapper-private. */
  id: MemberId;
  startedAt?: string;
  endedAt?: string;
  /** Case identity, linked post-check-in. */
  caseId?: CaseId;
}

export interface VisitProps {
  id: VisitId;
  status: VisitStatus;
  patientId: PatientId;
  window: TimeRangeProps;
  modality: VisitModality;
  kind: VisitKind;
  assignments: ResourceAssignment[];
  execution?: Execution;
  caseId?: CaseId;
  /**
   * The requests this visit carries out, as ONE reference. It replaced a
   * per-visit CarePlan whose only job was to point at them: the order set
   * already carries the encounter it applies to and the protocol it was
   * realized from, so the wrapper supplied no context of its own.
   */
  orderSetId?: OrderSetId;
  description?: string;
  serviceType?: CodeableConcept;
}

export type StoredVisitProps = Omit<VisitProps, "status"> & { status?: VisitStatus };

type BookingInput = Omit<VisitProps, "status" | "kind" | "assignments" | "execution"> & {
  status?: "booked";
  assignments: Array<Omit<ResourceAssignment, "id"> & { id?: MemberId }>;
  execution: Omit<Execution, "id"> & { id?: MemberId };
};

type ProtocolBookingInput = Omit<BookingInput, "modality" | "orderSetId"> & { orderSetId: OrderSetId };

// ── Transition table ──────────────────────────────────────────

type VisitEvent =
  | "CONFIRM"
  | "CHECK_IN"
  | "ADMIT"
  | "COMPLETE"
  | "CANCEL"
  | "NO_SHOW"
  | "RESCHEDULE"
  | "REPLACE_BOOKING";

const TRANSITIONS: Record<VisitStatus, Partial<Record<VisitEvent, VisitStatus>>> = {
  proposed: { CONFIRM: "booked", CANCEL: "cancelled" },
  booked: {
    CHECK_IN: "checkedIn",
    CANCEL: "cancelled",
    NO_SHOW: "noShow",
    RESCHEDULE: "booked",
    REPLACE_BOOKING: "booked",
  },
  // A patient who knocked and was never let in is a no-show, not a completed visit.
  checkedIn: { ADMIT: "inProgress", CANCEL: "cancelled", NO_SHOW: "noShow", REPLACE_BOOKING: "checkedIn" },
  inProgress: { COMPLETE: "completed", CANCEL: "cancelled", REPLACE_BOOKING: "inProgress" },
  completed: {},
  cancelled: {},
  noShow: {},
};

// ── The aggregate ─────────────────────────────────────────────

export class Visit {
  private state: Omit<VisitProps, "window" | "assignments"> & {
    window: TimeRange;
    assignments: Array<Omit<ResourceAssignment, "window"> & { window: TimeRange }>;
  };

  private constructor(props: VisitProps) {
    if (!props.patientId) {
      throw new PreconditionFailedError("Visit requires a patient");
    }
    let visitWindow: TimeRange;
    try {
      visitWindow = timeRange(props.window);
    } catch {
      throw new PreconditionFailedError("Visit window requires start < end");
    }
    // The kind and the order set agree, always — including on load, because a
    // visit that disagrees with itself is corruption rather than old data. This
    // is what the discriminated union used to buy: the illegal combinations
    // stay unrepresentable, without nesting the payload inside the tag.
    if (props.kind === "consultation") {
      if (props.orderSetId) {
        throw new PreconditionFailedError("A consultation carries no orders, so it cannot name an order set");
      }
    } else if (!props.orderSetId) {
      throw new PreconditionFailedError(`A '${props.kind}' visit requires the order set it carries out`);
    }

    // Booking rules — a NEW visit must be born valid; persisted state loads as-is.
    for (const assignment of props.assignments) {
      let assignmentWindow: TimeRange;
      try {
        assignmentWindow = timeRange(assignment.window);
      } catch {
        throw new PreconditionFailedError("Assignment window requires start < end");
      }
      if (!visitWindow.contains(assignmentWindow)) {
        throw new PreconditionFailedError("Assignment window must fall within the visit window");
      }
    }

    const cloned = structuredClone(props);
    this.state = {
      ...cloned,
      window: visitWindow,
      assignments: props.assignments.map(({ window: assignmentWindow, ...assignment }) => ({
        ...structuredClone(assignment),
        window: timeRange(assignmentWindow),
      })),
    };
  }

  static bookConsultation(input: Omit<BookingInput, "orderSetId">): Visit {
    return Visit.create({ ...input, kind: "consultation" });
  }

  static bookStandaloneProtocol(input: ProtocolBookingInput, facts: { caseHasCareManager: boolean }): Visit {
    if (!facts.caseHasCareManager) {
      throw new PreconditionFailedError("A standalone protocol booking requires a care manager on the case");
    }
    return Visit.create({ ...input, modality: "in-person", kind: "standalone" });
  }

  static bookCuratedProtocol(input: ProtocolBookingInput, facts: { caseHasCareManager: boolean }): Visit {
    if (!facts.caseHasCareManager) {
      throw new PreconditionFailedError("A curated protocol booking requires a care manager on the case");
    }
    return Visit.create({ ...input, modality: "in-person", kind: "curated-session" });
  }

  private static create(input: BookingInput & { kind: VisitKind }): Visit {
    if ((input.status ?? "booked") !== "booked") {
      throw new PreconditionFailedError(`Bookable visit must have status 'booked', got '${input.status}'`);
    }
    if (input.assignments.length === 0) {
      throw new PreconditionFailedError("Bookable visit requires at least one resource assignment");
    }
    if (!input.execution) {
      throw new PreconditionFailedError("Bookable visit requires an execution record");
    }
    if (input.execution.startedAt) {
      throw new PreconditionFailedError("Bookable visit requires an un-started execution");
    }
    // Seed past any ids carried in — `rescheduleTo` carries the surviving assignment's id and
    // leaves the rest to be minted, so a sequence starting at 0 hands the new member the id the
    // carried one already holds. Two Slots then share one member identity and the commit breaks.
    const assignmentIds = memberIdSequence("assignment", highWaterMark(input.assignments));
    const assignments = input.assignments.map((assignment) => ({
      ...structuredClone(assignment),
      id: assignment.id ?? assignmentIds.next(),
    }));
    const distinct = new Set(assignments.map((assignment) => assignment.id));
    if (distinct.size !== assignments.length) {
      throw new PreconditionFailedError("Visit assignments must each have a distinct member identity");
    }
    return new Visit({
      ...structuredClone(input),
      id: asVisitId(input.id),
      status: "booked",
      assignments,
      execution: { ...structuredClone(input.execution), id: input.execution.id ?? DEFAULT_VISIT_EXECUTION_MEMBER_ID },
    });
  }

  static restore(props: StoredVisitProps): Visit {
    // Historical snapshots without a status represent booked visits. Resolve
    // that policy here so every persisted snapshot carries the same decision.
    const status =
      props.modality === "in-person" && props.status === "checkedIn" ? "inProgress" : (props.status ?? "booked");
    return new Visit({ ...props, status });
  }

  // ── identity & facts ──

  get id(): VisitId {
    return this.state.id;
  }

  get status(): VisitStatus {
    return this.state.status;
  }

  get kind(): VisitKind {
    return this.state.kind;
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  get window(): TimeRangeProps {
    return serializeRange(this.state.window);
  }

  get assignments(): readonly ResourceAssignment[] {
    return this.state.assignments.map(({ window, ...assignment }) => ({
      ...structuredClone(assignment),
      window: serializeRange(window),
    }));
  }

  /** Stable parent-scoped identity of the execution member. */
  get executionId(): MemberId {
    return this.requireExecution("reference").id;
  }

  /**
   * The execution ref for charting — only a visit the clinician has actually
   * started can be charted against. Checking in is the knock; it unlocks nothing.
   */
  get chartableExecutionId(): MemberId {
    if (this.state.status !== "inProgress") {
      throw new PreconditionFailedError(
        `Visit must be in 'inProgress' status for clinical activity, currently '${this.state.status}'`,
      );
    }
    return this.executionId;
  }

  /**
   * The practitioner this visit belongs to. A practitioner may hold several
   * assignments — each is a reserved interval, not a participant — but a visit
   * that reserves two DIFFERENT practitioners has no single owner, and a
   * reschedule asking "whose calendar am I searching?" has no answer.
   */
  get assignedPractitionerId(): PractitionerId {
    const ids = new Set(
      this.state.assignments.flatMap((a) => (a.resource.type === "Practitioner" ? [a.resource.id] : [])),
    );
    const [only] = ids;
    if (ids.size !== 1 || !only) {
      throw new PreconditionFailedError(
        `Visit ${this.state.id ?? "(unsaved)"} reserves ${ids.size} practitioners, so it has no single assignee`,
      );
    }
    return only;
  }

  snapshot(): VisitProps {
    const { window, assignments, ...state } = this.state;
    return {
      ...structuredClone(state),
      window: serializeRange(window),
      assignments: assignments.map(({ window: assignmentWindow, ...assignment }) => ({
        ...structuredClone(assignment),
        window: serializeRange(assignmentWindow),
      })),
    };
  }

  // ── lifecycle ──

  /**
   * Check in any time before the appointment ends. In-person execution starts immediately;
   * virtual visits wait for admission to the conference call.
   */
  checkIn(now: Date): void {
    const next = this.nextStatus("CHECK_IN");
    const execution = this.requireExecution("check in");
    if (now > this.state.window.end) {
      throw new PreconditionFailedError("Cannot check in after appointment has ended");
    }
    this.state.status = this.state.modality === "in-person" ? "inProgress" : next;
    if (this.state.modality === "in-person") execution.startedAt = now.toISOString();
  }

  /**
   * Let the patient in (checkedIn → inProgress); stamps execution start.
   *
   * No window guard. The window governs whether someone may knock, not how late
   * a clinician may run — refusing a late admission would strand a waiting
   * patient with no legal move but no-show.
   */
  admit(now: Date): void {
    if (this.state.modality !== "virtual") {
      throw new PreconditionFailedError("Only virtual visits require admission to the conference call");
    }
    const next = this.nextStatus("ADMIT");
    const execution = this.requireExecution("admit");
    this.state.status = next;
    execution.startedAt = now.toISOString();
  }

  /** Complete (inProgress → completed); stamps execution end. */
  complete(now: Date): void {
    const next = this.nextStatus("COMPLETE");
    this.state.status = next;
    if (this.state.execution) this.state.execution.endedAt = now.toISOString();
  }

  /** Cancel (proposed/booked/checkedIn/inProgress → cancelled); frees every calendar reservation. */
  cancel(): void {
    const next = this.nextStatus("CANCEL");
    this.state.status = next;
    this.state.assignments = [];
  }

  /** Accept a freshly checked replacement, preserving the visit on conflict. */
  acceptBooking(check: BookingCheck): void {
    if (!check.bookable)
      throw new SlotConflictError("The proposed order set is no longer bookable. Check availability again.");
    this.replaceBooking(check.window, check.assignments);
  }

  /** Replace the window and assignments in place, keeping the visit and its execution history. */
  replaceBooking(window: TimeRangeProps, assignments: Array<Omit<ResourceAssignment, "id"> & { id?: MemberId }>): void {
    this.nextStatus("REPLACE_BOOKING");
    const nextAssignments = reconcileAssignments(this.state.assignments, assignments);
    // Validate both windows together before replacing state; a refusal leaves history and claims intact.
    this.state = new Visit({ ...this.snapshot(), window, assignments: nextAssignments }).state;
  }

  /**
   * Move this visit to a new time: cancel it, and return the visit that
   * replaces it.
   *
   * BOTH HALVES OR NEITHER. A reschedule is a cancel and a booking, and the
   * failures worth designing out are the halves — a freed calendar with no
   * visit on it, or two live visits holding the same orders. One call produces
   * both, so a caller cannot commit one and forget the other; committing them
   * in one transaction is still the caller's to arrange.
   *
   * The replacement is the SAME KIND of visit carrying out the SAME ORDER SET.
   * Nothing clinical is re-decided by moving a visit — the orders keep their
   * ids, so a set already charted against does not fork — and the kind is
   * carried across rather than re-derived, because after this the old visit is
   * cancelled and a standalone that re-books its own set is indistinguishable
   * from a curated session.
   *
   * The execution does NOT come along. The Encounter belongs to the visit that
   * was cancelled; the replacement gets a fresh one, unstarted.
   */
  rescheduleTo(to: {
    id: VisitId;
    window: TimeRangeProps;
    assignments: Array<Omit<ResourceAssignment, "id"> & { id?: MemberId }>;
  }): Visit {
    this.nextStatus("RESCHEDULE");
    const { id, status, execution, assignments, window, ...carried } = this.snapshot();
    const priorAssignments = this.assignments;
    this.cancel();

    return Visit.create({
      ...carried,
      id: to.id,
      status: "booked",
      window: to.window,
      assignments: reconcileAssignments(priorAssignments, to.assignments),
      execution: {},
    });
  }

  /** No-show (booked/checkedIn → noShow), only after the appointment start time. */
  markNoShow(now: Date): void {
    const next = this.nextStatus("NO_SHOW");
    if (now < this.state.window.start) {
      throw new PreconditionFailedError("Cannot mark no-show before appointment start time");
    }
    this.state.status = next;
  }

  // ── links (idempotent) ──

  /**
   * Link a case to the visit's execution.
   *
   * Two preconditions, on two aggregates: the visit needs an execution to hang
   * the link on, and the case must still be open. The second is not knowable
   * from here, so it is handed in as a fact rather than looked up — the same
   * shape as other cross-aggregate guards. A caller
   * that checks it separately and calls this afterwards leaves the guard
   * insufficient on its own, which is how a second encoding of the rule drifts
   * in.
   *
   * A bare fact, not the Case: the visit has no business knowing what a
   * case's statuses are or which of them count as open.
   */
  linkCase(caseId: CaseId, facts: CaseLinkFacts): void {
    const execution = this.requireExecution("link a case to");
    if (!facts.caseIsOpen) {
      throw new PreconditionFailedError("Cannot link an encounter to a case that is no longer open");
    }
    if (execution.caseId === caseId) return;
    execution.caseId = caseId;
    this.state.caseId = caseId;
  }

  /** Whether this visit's requests have been materialized — the idempotency fact. */
  get hasOrderSet(): boolean {
    return this.state.orderSetId !== undefined;
  }

  /**
   * The stable order set identity this visit carries out.
   */
  get orderSetId(): OrderSetId {
    if (!this.state.orderSetId) throw new PreconditionFailedError(`Visit ${this.state.id} has no order set`);
    return this.state.orderSetId;
  }

  // ── internals ──

  /** Resolve the target status for an event WITHOUT mutating — guards run before any state change. */
  private nextStatus(event: VisitEvent): VisitStatus {
    const target = TRANSITIONS[this.state.status]?.[event];
    if (!target) throw new InvalidTransitionError(this.state.status, event);
    return target;
  }

  private requireExecution(action: string): Execution {
    if (!this.state.execution) {
      throw new PreconditionFailedError(`Cannot ${action} a visit without an execution record`);
    }
    return this.state.execution;
  }
}

function timeRange(range: TimeRangeProps): TimeRange {
  return new TimeRange(new Date(range.start), new Date(range.end));
}

function serializeRange(range: TimeRange): TimeRangeProps {
  return { start: range.start.toISOString(), end: range.end.toISOString() };
}

/** Member identity is shared by in-place commits and deliberate reschedules. */
function reconcileAssignments(
  priorAssignments: readonly Pick<ResourceAssignment, "id" | "resource" | "calendarId" | "stepId">[],
  assignments: Array<Omit<ResourceAssignment, "id"> & { id?: MemberId }>,
): ResourceAssignment[] {
  const suppliedIds = assignments.flatMap((assignment) => (assignment.id ? [assignment.id] : []));
  if (new Set(suppliedIds).size !== suppliedIds.length) {
    throw new PreconditionFailedError("Visit assignments must each have a distinct member identity");
  }
  const assignmentIds = memberIdSequence(
    "assignment",
    highWaterMark([...priorAssignments, ...suppliedIds.map((id) => ({ id }))]),
  );
  const carriedIds = new Set<MemberId>(suppliedIds);
  return assignments.map((assignment) => {
    if (assignment.id) return { ...assignment, id: assignment.id };
    const prior = priorAssignments.find(
      (candidate) =>
        !carriedIds.has(candidate.id) &&
        candidate.resource.type === assignment.resource.type &&
        candidate.resource.id === assignment.resource.id &&
        candidate.calendarId === assignment.calendarId &&
        candidate.stepId === assignment.stepId,
    );
    if (prior) carriedIds.add(prior.id);
    return { ...assignment, id: prior?.id ?? assignmentIds.next() };
  });
}
